/**
 * Badge - Reusable badge component for status, labels, and tags
 * Can be used for showing status, counts, categories, etc.
 */

import React from 'react';
import { X } from 'lucide-react';

export interface BadgeProps {
  label: string;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
  size?: 'sm' | 'md' | 'lg';
  removable?: boolean;
  onRemove?: () => void;
  icon?: React.ReactNode;
  className?: string;
}

const Badge: React.FC<BadgeProps> = ({
  label,
  variant = 'default',
  size = 'md',
  removable = false,
  onRemove,
  icon,
  className = ''
}) => {
  const variantStyles = {
    default: 'bg-blue-100 text-blue-800 border-blue-200',
    success: 'bg-green-100 text-green-800 border-green-200',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    error: 'bg-red-100 text-red-800 border-red-200',
    info: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    neutral: 'bg-gray-100 text-gray-800 border-gray-200'
  };

  const sizeStyles = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base'
  };

  const style = variantStyles[variant];
  const sizeStyle = sizeStyles[size];

  return (
    <span className={`
      inline-flex items-center gap-1
      ${sizeStyle}
      ${style}
      font-medium
      rounded-full
      border
      ${className}
    `}>
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {label}
      {removable && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.();
          }}
          className="ml-1 -mr-0.5 hover:opacity-75 focus:outline-none"
        >
          <X className={size === 'sm' ? 'w-3 h-3' : size === 'md' ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
        </button>
      )}
    </span>
  );
};

export default Badge;